/**
 * Widget Auth Context
 *
 * Provides authentication state and methods for widget customer portal.
 * Stores JWT token in sessionStorage (separate from main app auth).
 * Parses JWT to extract user info (doesn't verify signature - backend does that).
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { WidgetApiClient } from '../services/widget/WidgetApiClient';
import { WidgetAuthService } from '../services/widget/WidgetAuthService';
import type { User, AuthResponse, RegisterFormData, LoginFormData } from '../types/widget/auth';

interface WidgetAuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: LoginFormData) => Promise<void>;
  register: (data: Omit<RegisterFormData, 'confirmPassword'>) => Promise<void>;
  logout: () => void;
  authService: WidgetAuthService | null;
}

const WidgetAuthContext = createContext<WidgetAuthContextType | undefined>(undefined);

const TOKEN_KEY = 'widget_auth_token';
const USER_KEY = 'widget_user';

export function WidgetAuthProvider({ children }: { children: React.ReactNode }) {
  const { providerSlug } = useParams<{ providerSlug: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authService, setAuthService] = useState<WidgetAuthService | null>(null);

  // Initialize API client and auth service
  useEffect(() => {
    if (!providerSlug) return;

    const getToken = () => sessionStorage.getItem(TOKEN_KEY);
    const apiClient = new WidgetApiClient(getToken);
    const service = new WidgetAuthService(providerSlug, apiClient);
    setAuthService(service);
  }, [providerSlug]);

  // Load user from storage on mount
  useEffect(() => {
    const storedToken = sessionStorage.getItem(TOKEN_KEY);
    const storedUser = sessionStorage.getItem(USER_KEY);

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(USER_KEY);
      }
    }

    setIsLoading(false);
  }, []);

  const handleAuthResponse = useCallback((response: AuthResponse) => {
    const { token, user } = response;

    // Store token and user in sessionStorage
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(USER_KEY, JSON.stringify(user));

    // Update state
    setToken(token);
    setUser(user);
  }, []);

  const login = useCallback(
    async (data: LoginFormData) => {
      if (!authService) throw new Error('Auth service not initialized');

      const response = await authService.login(data);
      handleAuthResponse(response);
    },
    [authService, handleAuthResponse]
  );

  const register = useCallback(
    async (data: Omit<RegisterFormData, 'confirmPassword'>) => {
      if (!authService) throw new Error('Auth service not initialized');

      const response = await authService.register(data);
      handleAuthResponse(response);
    },
    [authService, handleAuthResponse]
  );

  const logout = useCallback(() => {
    // Clear sessionStorage
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);

    // Clear state
    setToken(null);
    setUser(null);
  }, []);

  const value: WidgetAuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    login,
    register,
    logout,
    authService,
  };

  return <WidgetAuthContext.Provider value={value}>{children}</WidgetAuthContext.Provider>;
}

export function useWidgetAuth() {
  const context = useContext(WidgetAuthContext);
  if (context === undefined) {
    throw new Error('useWidgetAuth must be used within a WidgetAuthProvider');
  }
  return context;
}
